<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="admin-page">
    <div class="admin-header">
      <div>
        <h1 class="admin-title">🌱 服務管理</h1>
        <p class="admin-sub">管理所有服務項目</p>
      </div>
      <button class="btn-add" @click="openAdd">+ 新增服務</button>
    </div>

    <div class="admin-content">
      <div v-if="loading" class="loading">載入中...</div>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>Emoji</th>
            <th>服務名稱</th>
            <th>服務描述</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in services" :key="s.id">
            <td class="td-emoji">{{ s.emoji }}</td>
            <td class="td-name">{{ s.name }}</td>
            <td class="td-desc">{{ s.description }}</td>
            <td class="td-actions">
              <button class="btn-edit" @click="openEdit(s)">編輯</button>
              <button class="btn-delete" @click="deleteService(s)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ editMode ? "編輯服務" : "新增服務" }}</h2>
          <button class="modal-close" @click="closeModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>服務名稱 *</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="例：低維護植栽"
              />
            </div>
            <div class="form-group">
              <label>Emoji</label>
              <input v-model="form.emoji" type="text" placeholder="例：🌱" />
            </div>
          </div>
          <div class="form-group">
            <label>服務描述</label>
            <textarea
              v-model="form.description"
              placeholder="服務描述..."
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">取消</button>
          <button class="btn-save" @click="saveService">
            {{ saving ? "儲存中..." : "儲存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import axios from "axios";

const services = ref([]);
const loading = ref(true);
const showModal = ref(false);
const editMode = ref(false);
const saving = ref(false);

const form = reactive({
  id: null,
  name: "",
  emoji: "🌱",
  description: "",
});

const resetForm = () => {
  form.id = null;
  form.name = "";
  form.emoji = "🌱";
  form.description = "";
};

const loadServices = async () => {
  try {
    const response = await axios.get("/services");
    services.value = response.data;
  } catch (error) {
    console.error("載入失敗:", error);
  } finally {
    loading.value = false;
  }
};

const openAdd = () => {
  resetForm();
  editMode.value = false;
  showModal.value = true;
};

const openEdit = (service) => {
  Object.assign(form, service);
  editMode.value = true;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  resetForm();
};

const saveService = async () => {
  if (!form.name) {
    alert("請填寫服務名稱");
    return;
  }

  saving.value = true;
  try {
    if (editMode.value) {
      await axios.put(`/services/${form.id}`, form);
      alert("服務已更新！");
    } else {
      // eslint-disable-next-line no-unused-vars
      const { id, ...newService } = form;
      await axios.post("/services", newService);
      alert("服務已新增！");
    }
    closeModal();
    await loadServices();
  } catch (error) {
    console.error("儲存失敗:", error);
    alert("儲存失敗，請稍後重試");
  } finally {
    saving.value = false;
  }
};

const deleteService = async (service) => {
  if (!confirm(`確定要刪除「${service.name}」嗎？`)) return;

  try {
    await axios.delete(`/services/${service.id}`);
    alert("服務已刪除！");
    await loadServices();
  } catch (error) {
    console.error("刪除失敗:", error);
    alert("刪除失敗，請稍後重試");
  }
};

onMounted(() => {
  loadServices();
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.admin-page {
  padding: 40px;
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
}
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.admin-title {
  font-size: 1.8em;
  color: #1c2b1a;
  margin-bottom: 4px;
}
.admin-sub {
  color: #6b7f68;
  font-size: 14px;
}
.btn-add {
  padding: 12px 24px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}
.btn-add:hover {
  background: #2d5016;
}
.admin-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.admin-table {
  width: 100%;
  border-collapse: collapse;
}
.admin-table th {
  background: #f5f7fa;
  padding: 14px 16px;
  text-align: left;
  font-size: 13px;
  color: #6b7f68;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}
.admin-table td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.admin-table tr:last-child td {
  border-bottom: none;
}
.admin-table tr:hover td {
  background: #f9fafb;
}
.td-emoji {
  font-size: 28px;
  text-align: center;
  width: 60px;
}
.td-name {
  font-weight: 600;
  color: #1c2b1a;
}
.td-desc {
  color: #6b7f68;
  font-size: 14px;
}
.td-actions {
  display: flex;
  gap: 8px;
}
.btn-edit {
  padding: 6px 16px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-edit:hover {
  background: #5a6fd6;
}
.btn-delete {
  padding: 6px 16px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-delete:hover {
  background: #ff5252;
}
.loading {
  padding: 60px;
  text-align: center;
  color: #9baa97;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: white;
  border-radius: 16px;
  width: 500px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #eee;
}
.modal-header h2 {
  font-size: 18px;
  color: #1c2b1a;
}
.modal-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #9baa97;
}
.modal-body {
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #3d4f3a;
}
.form-group input,
.form-group textarea {
  padding: 10px 14px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
}
.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4e7b4f;
}
.form-group textarea {
  min-height: 100px;
  resize: vertical;
}
.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.btn-cancel {
  padding: 10px 24px;
  background: #f5f7fa;
  color: #6b7f68;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.btn-save {
  padding: 10px 24px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}
.btn-save:hover {
  background: #2d5016;
}
</style>
